import { InputTextarea } from 'primereact/inputtextarea'
import React, { SyntheticEvent, useEffect, useRef } from 'react'

import useImages from '../../../modules/images/useImages'
import { IRadioButtonsQuestionSchema, RadioButtonsResponse } from '../../RadioButtons/types'
import { ICheckboxesQuestionSchema, CheckboxesResponse } from '../../Checkboxes/types'
import { QuestionType } from '../../types'

import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { getPlainTextFromFormatted } from '../../../lib/accessibility'
import { hapticEffects } from '../../../lib/hapticEffects'

import useSurveyLocale from '../../../modules/locale/useSurveyLocale'
import useSession from '../../../session/useSession'
import useViewport from '../../../modules/viewport/useViewport'
import usePreviewOptions from '@aytm/app/swidget/base/modules/preview/usePreviewOptions'
import { ViewportOrientation } from '../../../modules/viewport/types'
import TextWithOptions from '@aytm/app/swidget/base/modules/preview/components/TextWithOptions'
import RotateDevice from '../RotateDevice/RotateDevice'

import style from './style.module.scss'
import commonStyle from '../../../questions/_common/style.module.scss'

const onCommentFocus = (event: SyntheticEvent) => {
  const el = event.target as HTMLInputElement
  if (!el) return
  el.setSelectionRange(el.value.length, el.value.length)
  hapticEffects.soft()
}

export interface GridProps {
  onSelectedChange: (answerIndex: number) => void
  onCommentChange: (answerIndex: number, event: React.ChangeEvent<HTMLTextAreaElement>) => void
  schema: IRadioButtonsQuestionSchema | ICheckboxesQuestionSchema
  response: RadioButtonsResponse | CheckboxesResponse
  na?: boolean
  showValidation?: boolean
  role?: string
}

export default function Grid({
  onSelectedChange: onCheckedChange,
  onCommentChange,
  response,
  schema,
  na,
  showValidation,
  role
}: GridProps) {
  const { ImageAsset } = useImages()
  const checkedAnswer = response.find(answer => answer.selected)
  const checkedIndex = checkedAnswer ? schema.answers.findIndex(answer => answer.id == checkedAnswer.id) : null
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const previewOptions = usePreviewOptions()

  const inputReference = useRef(null)

  useEffect(() => {
    const el = inputReference.current as HTMLInputElement
    if (!el) return
    el.focus()
    el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    // setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' }), 200)
  }, [checkedAnswer])

  const onCheckedChangeInternal = (answerIndex: number, event: SyntheticEvent) => {
    const el = event?.target as Element
    if (
      event?.target &&
      ('I' == el.tagName ||
        el.classList.contains('p-component-overlay') ||
        el.classList.contains('p-image-preview') ||
        el.parentElement?.classList.contains('p-image-preview-enter-done'))
    ) {
      return
    }
    onCheckedChange(answerIndex)
  }

  const session = useSession()
  useEffect(() => {
    if (!viewport.mobile && schema.image_grid_columns && schema.image_grid_columns > 2)
      session.setWidgetWidth(Math.min(viewport.width, schema.image_grid_columns > 3 ? 1000 : 900))
  }, [session, schema, viewport.mobile, viewport.width])

  const turn = viewport.mobile && viewport.orientation == ViewportOrientation.PORTRAIT && schema.image_grid_columns > 2
  const cellWidth =
    (viewport.mobile ? viewport.width : (viewport.widgetWidth as number) - 50) / schema.image_grid_columns
  return (
    <div className={style.grid} data-testid="SW-Question-ImageGrid">
      {turn ? (
        <RotateDevice />
      ) : (
        schema.answers
          .filter(answer => !answer.na)
          .map((answer, answerIndex) => {
            const responseAnswer = response[answerIndex]
            const bigSrc = getMediumAssetPath(answer.image)
            return (
              <button
                key={answer.id}
                className={`${style.upContainer} ${answer.hidden ? ' ' + commonStyle.hidden : ''} ${
                  answer.fakeHidden ? ' ' + commonStyle.fakeHidden : ''
                }`}
                data-testid="SW-Question-ImageGrid-ImageContainer"
                role={role}
                aria-checked={responseAnswer.selected && !na}
                style={{ width: `calc(${100 / schema.image_grid_columns}% - 5px)` }}
                onClick={event => onCheckedChangeInternal(answerIndex, event)}
              >
                <div
                  className={`${style.container} ${na ? ' ' + style.disabled : ''}  ${
                    viewport.mobile ? ' ' + style.mobile : ''
                  } ${responseAnswer.selected && !na ? ' ' + style.checked : ''}`}
                  style={{
                    backgroundImage: `url(${bigSrc})`
                  }}
                  data-testid="SW-Question-ImageGrid-Image"
                >
                  <ImageAsset
                    asset={answer.image}
                    size="medium"
                    width={cellWidth}
                    height={cellWidth}
                    previewLabelTemplate={() => (
                      <TextWithOptions
                        text={answer.text}
                        options={previewOptions}
                        rules={session.rules?.sub?.[schema.id]?.[answer.id]}
                      />
                    )}
                  />
                </div>
                <span className={style.text} data-testid="SW-Question-ImageGrid-Text">
                  {(previewOptions.showHidden || !session.rules?.hide_text?.[answer.id]?.condition) && (
                    <TextWithOptions
                      text={answer.text}
                      options={previewOptions}
                      rules={session.rules?.sub?.[schema.id]?.[answer.id]}
                    />
                  )}
                </span>
              </button>
            )
          })
      )}
      {QuestionType.RADIO_BUTTONS == schema.type &&
        checkedAnswer &&
        schema.answers[checkedIndex].oe != 'off' &&
        !turn && (
          <InputTextarea
            data-testid="SW-RB-OE"
            className={`${style.textArea} ${
              showValidation && !checkedAnswer?.comment?.length && 'mandatory' == schema.answers[checkedIndex].oe
                ? style.error
                : ''
            }`}
            placeholder={getPlainTextFromFormatted(t('questions', 'placeholder'))}
            rows={3}
            autoResize={true}
            // autoFocus={true}
            ref={inputReference}
            onFocus={onCommentFocus}
            value={checkedAnswer.comment || ''}
            onChange={event => onCommentChange(checkedIndex, event)}
          />
        )}
    </div>
  )
}
